import { useGetEnrollmentAuditLog } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import {
  EnrollmentAuditLogDTO,
  EnrollmentAuditLogEventDTO,
} from '@shared/clientTypes/DTOs/EnrollmentsDTO';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import SessionModalSection from '../../DetailsModalSection';

interface EnrollmentChangeLogSectionProps {
  enrollmentId: string;
}

const OPERATION_LABELS: Record<EnrollmentAuditLogDTO['operation'], string> = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
};

const actorLabel = (actor: EnrollmentAuditLogDTO['actor']): string => actor.name ?? 'Unknown admin';

const EnrollmentChangeLogSection = (props: EnrollmentChangeLogSectionProps) => {
  const { enrollmentId } = props;

  const { data: entries, isLoading } = useGetEnrollmentAuditLog(enrollmentId);

  return (
    <SessionModalSection sectionTitle={'Admin Change Log'} className="flex flex-col gap-1">
      {isLoading && <LoadingSpinner className="mx-auto" />}

      {!isLoading && !entries?.length && (
        <p className="text-sm text-customGrey">No changes recorded yet.</p>
      )}

      {!isLoading &&
        entries?.map((entry: EnrollmentAuditLogDTO, index: number) => (
          <div
            key={entry._id}
            className={mergeClassNames('flex flex-col px-2 py-1 bg-surfaceBackground', {
              'bg-surfaceHover': index === 0,
            })}
          >
            <div className="flex items-center gap-1">
              <span
                className={mergeClassNames('text-sm font-bold text-customPrimary', {
                  'text-customRedAccent': entry.operation === 'delete',
                })}
              >
                {OPERATION_LABELS[entry.operation]}
              </span>
              <span className="text-xs text-customGrey">
                {moment(entry.createdAt).format('DD MMM YYYY, HH:mm')}
              </span>
              <span className="text-xs text-customGrey">
                by<span className="font-bold"> {actorLabel(entry.actor)}</span>
              </span>
            </div>

            {entry.events?.map((event: EnrollmentAuditLogEventDTO, eventIndex: number) => (
              <div key={`${entry._id}-${eventIndex}`} className="flex flex-wrap gap-1 text-sm">
                <span className="text-customGrey">{event.text}</span>
                {(event.from !== undefined || event.to !== undefined) && (
                  <>
                    <span className="text-customRedAccent line-through">{event.from ?? '—'}</span>
                    <span className="text-customGrey">→</span>
                    <span className="text-customPrimary">{event.to ?? '—'}</span>
                  </>
                )}
              </div>
            ))}
          </div>
        ))}
    </SessionModalSection>
  );
};

export default EnrollmentChangeLogSection;
